import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FILE_TOO_LARGE } from 'utils/constants/errors'
import { maxImageUploadBytes, maxVideoUploadBytes } from 'utils/constants/file'
import { getFileTypeFromMimeType } from 'utils/media'

import { FileType } from 'types/file'
import { Media } from 'types/media'

interface Return {
  handleFilesSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleReset: () => void
  selectedFiles?: SelectedFile[]
  selectedFilesRef?: React.MutableRefObject<SelectedFile[] | undefined>
  selectedFileTypes?: FileType[]
  selectedFileUrls?: string[]
}

type SelectedFile = File | Media

export const useMultipleFilesSelect = (initialFiles?: SelectedFile[]): Return => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[] | undefined>(
    initialFiles ?? undefined,
  )

  const selectedFilesRef = useRef<SelectedFile[] | undefined>(selectedFiles)

  const handleFilesSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files?.length) {
      const files = Array.from(event.target.files)

      files.forEach((file) => {
        const maxUploadSize =
          getFileTypeFromMimeType(file.type) === 'image' ? maxImageUploadBytes : maxVideoUploadBytes

        if (file.size > maxUploadSize) {
          throw new Error(FILE_TOO_LARGE.message)
        }
      })

      setSelectedFiles(files as SelectedFile[])
    }
  }, [])

  const handleReset = useCallback(() => {
    setSelectedFiles(undefined)
  }, [])

  const selectedFileUrls = useMemo(() => {
    if (!selectedFiles) {
      return undefined
    }

    const fileUrls: string[] = []
    selectedFiles.forEach((file) => {
      if (!file) return
      const url = 'url' in file ? file.url : URL.createObjectURL(file)
      fileUrls.push(url)
    })

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return fileUrls
  }, [selectedFiles])

  const selectedFileTypes = useMemo(() => {
    if (!selectedFiles) {
      return undefined
    }

    const fileTypes: FileType[] = []
    selectedFiles.forEach((file) => {
      if (!file) return
      const type =
        'mimeType' in file
          ? getFileTypeFromMimeType(file.mimeType)
          : getFileTypeFromMimeType(file.type)
      fileTypes.push(type)
    })

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return fileTypes
  }, [selectedFiles])

  useEffect(() => {
    selectedFilesRef.current = selectedFiles
  }, [selectedFiles])

  useEffect(() => {
    setSelectedFiles(initialFiles)
  }, [initialFiles?.length])

  return {
    handleReset,
    handleFilesSelect,
    selectedFiles,
    selectedFilesRef,
    selectedFileTypes,
    selectedFileUrls,
  }
}
